import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit } from '@angular/core';
import { assetTypeDefinition } from '../../../../entity-definitions/asset-type.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { ToastService } from '../../../../services/toast.service';
import { AssetTypeStore } from '../../../../stores/asset-type.store';
import { AssetStore } from '../../../../stores/asset.store';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Asset } from '../../../../types/asset.interface';
import { AssetType } from '../../../../types/asset-type.interface';
import { isEntityFormDeleteAction } from '../../../../types/entity-form-result.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesIconComponent } from '../../../ui/ces-icon/ces-icon.component';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

@Component({
  selector: 'app-asset-types-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTitleComponent, GenericParentGridComponent, CesButtonComponent, CesIconComponent, TranslocoModule],
  templateUrl: './asset-types-list.component.html',
})
export class AssetTypesListComponent implements OnInit {
  protected readonly assetTypeStore = inject(AssetTypeStore);
  protected readonly assetStore = inject(AssetStore);
  private readonly dialogService = inject(EntityDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  assetTypeDefinition = assetTypeDefinition;
  assetTypes: AssetType[] = [];

  dataLoadingCompleted: boolean = false;
  loadFailed: boolean = false;

  protected readonly assetCountByType = computed(() => {
    const result: Record<number, number> = {};
    const assets = this.assetStore.entities();

    this.assetTypes.forEach(type => {
      result[type.id] = 0;
    });

    assets.forEach((asset: Asset) => {
      result[asset.assetTypeId] = (result[asset.assetTypeId] ?? 0) + 1;
    });

    return result;
  });

  ngOnInit(): void {
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.loadFailed = false;
    try {
      await this.assetTypeStore.loadAll();
      this.assetTypes = this.assetTypeStore.entities();
      await this.assetStore.loadAll();
      this.dataLoadingCompleted = true;
    } catch (error) {
      this.loadFailed = true;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  createAssetType(): void {
    this.dialogService
      .openCreateDialog<AssetType>({
        header: this.assetTypeDefinition.messages.dialogTitles.create(),
        entityDefinition: this.assetTypeDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (formData && !isEntityFormDeleteAction(formData)) {
          void (async () => {
            try {
              await this.assetTypeStore.create(formData);
              await this.loadData();
            } catch (error) {
              console.error(this.translateService.translate('common.errors.assetTypeCreationFailed'), error);
            }
          })();
        }
      });
  }

  editAssetType(assetType: AssetType): void {
    this.dialogService
      .openEditDialog<AssetType>(assetType, {
        header: this.assetTypeDefinition.messages.dialogTitles.edit(),
        headerParams: { name: assetType.name },
        entityDefinition: this.assetTypeDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteAssetType(entity),
      })
      .subscribe(result => {
        if (result) {
          if (isEntityFormDeleteAction(result)) {
            this.deleteAssetType(result.entity);
          } else {
            void this.assetTypeStore.update(assetType.id, result as Partial<AssetType>).then(() => {
              void this.loadData();
            });
          }
        }
      });
  }

  deleteAssetType(assetType: AssetType): void {
    const assetCount = this.getAssetCount(assetType.id);

    if (assetCount > 0 || assetType.isUsed) {
      this.toastService.warning('common.errors.cannotDeleteUsedAssetType');
      return;
    }

    void this.assetTypeStore
      .delete(assetType.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.assetType.messages.deleteError');
        console.error(this.translateService.translate('common.errors.assetTypeDeletionFailed'), error);
      });
  }

  getAssetCount(typeId: number): number {
    return this.assetCountByType()[typeId] ?? 0;
  }
}
